import PropTypes from 'prop-types';
import { forwardRef, useMemo } from 'react';
// @mui
import Popper from '@mui/material/Popper';
import { styled } from '@mui/material/styles';

// ----------------------------------------------------------------------
//
// Popper used by every date / time picker (wired in through the theme, see
// theme/overrides/components/date-picker.js).
//
// Why it exists
// -------------
// The stock picker popper is `position: absolute` inside <body> and uses
// Popper.js `flip`. That combination is unstable on short pages:
//
//   1. popper opens below the field and hangs past the end of the document
//   2. the document grows -> a vertical scrollbar appears -> viewport is ~15px
//      narrower -> the form reflows -> the field moves
//   3. `flip` re-measures, picks the other side, the popper no longer
//      overflows, the scrollbar disappears, the layout reflows back -> goto 1
//
// What it does instead
// --------------------
//  * `strategy: 'fixed'`: the popper can never change the scrollable overflow
//    of the document, so it can never add / remove a page scrollbar.
//  * `flip` is replaced by `adaptivePlacement`, a pure function of the field's
//    rect, the viewport and the content's natural height. It does not read the
//    popper's current (possibly clamped) size, so placement cannot oscillate.
//  * If neither side has enough room, the roomier side is used and the paper
//    gets a max-height and scrolls internally (a genuine scrollbar), instead
//    of running off-screen.
//  * A ResizeObserver re-runs positioning when the content really changes
//    size (month <-> year view); it converges because the size does not depend
//    on the position.
//
// ----------------------------------------------------------------------

const VIEWPORT_PADDING = 8; // px kept free between popper and viewport edge
const MIN_HEIGHT = 80; // last-resort scroll: never clamp the paper below this (px)
const MIN_SCALE = 0.5; // shrink the calendar down to this factor before resorting to scrolling

export const PICKER_POPPER_MAX_HEIGHT_VAR = '--picker-popper-max-height';
export const PICKER_POPPER_OVERFLOW_VAR = '--picker-popper-overflow';
export const PICKER_POPPER_ZOOM_VAR = '--picker-popper-zoom';

// Full height of the popper content, independent of any max-height we applied.
function getNaturalHeight(popper) {
  let total = 0;
  Array.from(popper.children).forEach((child) => {
    total += child.scrollHeight;
  });
  return total;
}

// Bottom edge of a fixed / sticky app bar pinned to the top of the page. That strip is
// not usable space, so a popper must not open over it. Ignored inside dialogs (backdrop covers it).
function getTopInset(doc, reference) {
  if (reference.closest?.('[role="dialog"], .MuiDialog-root, .MuiDrawer-root')) return 0;
  let inset = 0;
  doc.querySelectorAll('.MuiAppBar-root').forEach((bar) => {
    const { position } = doc.ownerDocument.defaultView.getComputedStyle(bar);
    if (position !== 'fixed' && position !== 'sticky') return;
    const r = bar.getBoundingClientRect();
    if (r.top <= 1 && r.bottom > 0 && r.width > 0) inset = Math.max(inset, r.bottom);
  });
  return inset;
}

const adaptivePlacement = {
  name: 'adaptivePlacement',
  enabled: true,
  phase: 'main',
  requiresIfExists: ['offset'],
  fn({ state, name }) {
    const data = state.modifiersData[name];
    // Popper.js re-runs all modifiers after `state.reset = true`; resolve once per update.
    if (data._skip) return;

    const { popper, reference } = state.elements;
    const [base, variation] = state.options.placement.split('-');
    if ((base !== 'top' && base !== 'bottom') || !reference?.getBoundingClientRect) return;

    const doc = popper.ownerDocument.documentElement;
    const viewportHeight = doc.clientHeight;
    const topInset = getTopInset(doc, reference);
    const rect = reference.getBoundingClientRect();
    // Measure at 100% scale so the result never depends on our own shrinking.
    popper.style.removeProperty(PICKER_POPPER_ZOOM_VAR);
    const natural = getNaturalHeight(popper);

    const space = {
      bottom: viewportHeight - rect.bottom - VIEWPORT_PADDING,
      top: rect.top - topInset - VIEWPORT_PADDING,
    };
    const other = base === 'bottom' ? 'top' : 'bottom';

    // Preferred side if it fits, else the other side if it fits, else the roomier one.
    let side = base;
    if (space[base] < natural) {
      side = space[other] >= natural || space[other] > space[base] ? other : base;
    }

    // Not enough room on the chosen side: shrink the calendar to fit (no scrollbar).
    // Only if it would need to shrink below MIN_SCALE do we fall back to scrolling inside it.
    const scale = space[side] / natural;
    const shrink = natural > space[side] && scale >= MIN_SCALE;
    const clamp = natural > space[side] && !shrink;
    if (shrink) popper.style.setProperty(PICKER_POPPER_ZOOM_VAR, String(Math.floor(scale * 100) / 100));
    if (clamp) {
      popper.style.setProperty(PICKER_POPPER_MAX_HEIGHT_VAR, `${Math.max(space[side], MIN_HEIGHT)}px`);
      popper.style.setProperty(PICKER_POPPER_OVERFLOW_VAR, 'auto');
    } else {
      popper.style.removeProperty(PICKER_POPPER_MAX_HEIGHT_VAR);
      popper.style.removeProperty(PICKER_POPPER_OVERFLOW_VAR);
    }

    // Our style change may have resized the popper after Popper.js measured it.
    const height = popper.offsetHeight;
    const resized = Math.abs(height - state.rects.popper.height) > 0.5;
    if (resized) state.rects.popper = { ...state.rects.popper, height };

    const placement = variation ? `${side}-${variation}` : side;
    if (resized || placement !== state.placement) {
      data._skip = true;
      state.placement = placement;
      state.reset = true;
    }
  },
  // Re-position when the content genuinely changes size (e.g. month -> year view).
  effect({ state, instance }) {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => instance.update());
    observer.observe(state.elements.popper);
    return () => observer.disconnect();
  },
};

const STABLE_MODIFIERS = [
  { name: 'flip', enabled: false },
  { name: 'preventOverflow', options: { padding: VIEWPORT_PADDING, altAxis: false } },
  adaptivePlacement,
];

const PickerPopperRoot = styled(Popper, { name: 'MuiPickersPopper', slot: 'Root' })(({ theme }) => ({
  zIndex: theme.zIndex.modal,
}));

const PickerPopper = forwardRef(({ modifiers, popperOptions, ...other }, ref) => {
  const mergedModifiers = useMemo(() => [...STABLE_MODIFIERS, ...(modifiers || [])], [modifiers]);
  const mergedOptions = useMemo(() => ({ ...popperOptions, strategy: 'fixed' }), [popperOptions]);

  return (
    <PickerPopperRoot
      ref={ref}
      {...other}
      modifiers={mergedModifiers}
      popperOptions={mergedOptions}
    />
  );
});

PickerPopper.propTypes = {
  modifiers: PropTypes.array,
  popperOptions: PropTypes.object,
};

export default PickerPopper;

// Theme override: the paper scrolls internally only when adaptivePlacement had to clamp it.
export const pickerPopperStyleOverrides = {
  MuiPickersPopper: {
    styleOverrides: {
      paper: {
        zoom: `var(${PICKER_POPPER_ZOOM_VAR}, normal)`,
        maxHeight: `var(${PICKER_POPPER_MAX_HEIGHT_VAR}, none)`,
        overflowY: `var(${PICKER_POPPER_OVERFLOW_VAR}, visible)`,
      },
    },
  },
};
